// src/utils/openaiService.js

/**
 * Groq AI Service for generating explanations
 * Provides AI-powered explanations for incorrect answers using Groq's LLaMA model
 */

import { fetchExplanationFromGROQ } from "./groqService";

const GROQ_API_URL = "https://api.groq.com/openai/v1/chat/completions";
const GROQ_API_KEY = "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";

/**
 * Generate explanation for an incorrect answer using Groq AI
 * @param {number} questionId - The question ID
 * @param {string} question - The question text
 * @param {string} studentAnswer - The student's answer
 * @param {string} correctAnswer - The correct answer
 * @returns {Promise<string>} - The explanation
 */
export const generateExplanation = async (
  questionId,
  question,
  studentAnswer,
  correctAnswer
) => {
  try {
    // Debug: Check if API key is loaded
    console.log("🔑 Checking GROQ_API_KEY...", {
      isSet: !!GROQ_API_KEY,
      envVarName: "REACT_APP_GROQ_API_KEY",
      keyLength: GROQ_API_KEY ? GROQ_API_KEY.length : 0
    });
    
    if (!GROQ_API_KEY) {
      console.error("❌ GROQ API KEY is not set. Please add REACT_APP_GROQ_API_KEY to your .env file and restart the server.");
      throw new Error("GROQ API KEY is not configured. Please set REACT_APP_GROQ_API_KEY in your .env file.");
    }
    
    console.log("✅ Using Groq API with key:", GROQ_API_KEY.substring(0, 10) + "...");
    
    // Handle missing or undefined question/answer data
    const questionText = question || "Question text not available";
    const studentAns = studentAnswer || "Not answered";
    const correctAns = correctAnswer || "Not provided";
    
    console.log("📝 Generating explanation for:", {
      question: questionText.substring(0, 100),
      studentAnswer: studentAns,
      correctAnswer: correctAns
    });
    
    const isCorrect = studentAns?.toLowerCase() === correctAns?.toLowerCase();
    const statusText = isCorrect ? "correct" : "incorrect";
    
    const userPrompt = `Question: ${questionText}

Student's Answer: ${studentAns}
Correct Answer: ${correctAns}
The student's answer is ${statusText}.

${isCorrect 
  ? "Please explain why this answer is correct and provide insights to reinforce the student's understanding."
  : `Please explain why the correct answer is "${correctAns}" and help the student understand their mistake.`}`;

    console.log("🚀 Making API call to Groq...", GROQ_API_URL);

    const response = await fetch(GROQ_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${GROQ_API_KEY}`,
      },
      body: JSON.stringify({
        model: "llama-3.3-70b-versatile",
        messages: [
          {
            role: "system",
            content: `You are an expert IELTS (International English Language Testing System) tutor helping students prepare for their English test. 
Your role is to explain why answers are correct or incorrect in a clear, educational manner.
Keep explanations concise but thorough (2-4 sentences).
Focus on:
1. Why the correct answer is right
2. If the student was wrong, explain the common misconception or error
3. Provide a tip for similar questions in the future
Use clear, professional English appropriate for IELTS test preparation.`
          },
          {
            role: "user",
            content: userPrompt
          }
        ],
        max_tokens: 300,
        temperature: 0.7
      }),
    });

    console.log("📡 API Response status:", response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("❌ Groq API error:", {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(`Groq API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    console.log("✅ Groq API response received:", {
      hasChoices: !!data.choices,
      choicesLength: data.choices?.length
    });
    
    const explanation = data.choices?.[0]?.message?.content;
    
    if (!explanation) {
      console.error("❌ No explanation in response:", data);
      throw new Error("No explanation generated from Groq AI");
    }
    
    console.log("✅ Explanation generated successfully");
    return explanation.trim();
  } catch (error) {
    console.error("❌ Error generating explanation:", {
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

/**
 * Generate explanation with retry logic
 * @param {string} question - The question text
 * @param {string} studentAnswer - The student's answer
 * @param {string} correctAnswer - The correct answer
 * @param {number} maxRetries - Maximum number of retries (default: 3)
 * @returns {Promise<string>} - The explanation
 */

export const generateExplanationWithRetry = async (
  question,
  studentAnswer,
  correctAnswer,
  maxRetries = 3
) => {
  // First try GROQ lookup (fast, returns curated explanations if available)
  try {
    const groqExplanation = await fetchExplanationFromGROQ(question);
    if (groqExplanation) return groqExplanation;
  } catch (err) {
    // Ignore and fallback to AI generation
    console.warn("GROQ lookup failed, falling back to AI generation", err);
  }

  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const explanation = await generateExplanation(
        null, // questionId not needed
        question,
        studentAnswer,
        correctAnswer
      );
      return explanation;
    } catch (error) {
      lastError = error;
      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries) {
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, attempt - 1) * 1000)
        );
      }
    }
  }

  throw lastError || new Error("Failed to generate explanation after retries");
};

export default generateExplanationWithRetry;
